import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Box from '@mui/material/Box';
import { CircularProgress, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import '../../App.css';
import Header from '../../components/HeaderComponent';
import ProgressControl from '../../components/ProgressControl';
import { Item, Item2 } from '../../styles';
import ErrorDialog from '../../components/ErrorDialog';
import WebSocketConnection from '../Sockets/WebSocketConnection';
import { getPreview } from '../../services/Spectrum';


type PredictSpectrumResultProps = {
  location: {
    state: {
      molecule: string;
    }
  }
};

const PredictSpectrumResult: React.FC<PredictSpectrumResultProps> = (props) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isErrorDialogOpened, setErrorDialogOpened] = useState<boolean>(false);
  const [moleculeSvgContent, setMoleculeSvgContent] = useState<string | null>(null);
  const [peaks, setPeaks] = useState<any[]>([]);
  const [spectrumPreview, setSpectrumPreview] = useState(null);
  const [atomPeaks, setAtomPeaks] = useState([]);

  const svgString = encodeURIComponent(moleculeSvgContent);
  const moleculeDataUri = `url("data:image/svg+xml,${svgString}")`;
  
  const pngString = encodeURIComponent(spectrumPreview?.graph ? spectrumPreview.graph : '');
  const spectrumDataUri = `data:image/png;base64,${pngString}`;

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      width: 0,
      hide: true
    },
    {
      field: 'atom',
      headerName: 'Atom N',
      flex: 0.3,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'shift',
      headerName: 'Chemical shift',
      flex: 0.7,
      disableColumnMenu: true,
      editable: false,
      sortable: false,
    },
  ];

  const onError = (errorPacket: any) =>
    showError(errorPacket.message);


  useEffect(() => {
    const fetchSpectrumPicture = async () => {
      const preview = await getPreview({
        "spectrum_type": 'C_NMR',
        "peaks": peaks,
        "solvent": '',
        "is_complete": false,
        "spectrumString": "",
        "structure": "",
      });
      if (preview) {
        setSpectrumPreview(preview);
        setLoading(false);
      } else {
        console.error('Failed to fetch spectrum preview data');
        setLoading(false);
      }
    };
    fetchSpectrumPicture();
  }, [peaks]);


  const onAccept = (packet: any) => {
    setMoleculeSvgContent(packet.svg);
    
    const atomPeaksArray = [];
    if (packet.result && typeof packet.result === 'object') {
      let id = 0;
      for (const shift in packet.result) {
        if (packet.result.hasOwnProperty(shift)) {
          atomPeaksArray.push({
            id: id++,
            shift: shift,
            atom: Array.isArray(packet.result[shift]) ? packet.result[shift].join(', ') : packet.result[shift].toString()
          });
        }
      }
    }
    setAtomPeaks(atomPeaksArray.sort((a, b) => {
      return a.shift - b.shift;
    }));
    
    let flattenedPeaks: any[] = [];
    if (packet.result && typeof packet.result === 'object') {
      for (const key in packet.result) {
        if (packet.result.hasOwnProperty(key)) {
          flattenedPeaks.push(Number(key));
        }
      }
    }
    setPeaks(flattenedPeaks);
  }

  useEffect(() => {
    WebSocketConnection.predict('spectrum', {molfile: props.location.state.molecule }, onAccept, onError, '', true);
  }, []);


  const showError = (message: string) => {
    setErrorMessage(message);
    setErrorDialogOpened(true);
  };

  const onCloseErrorDialog = () => {
    setErrorDialogOpened(false);
  };

  return (
    <Grid container spacing={0} className="main-frame">
      <Grid container md={12}>
        <Item2><div style={{ height: '2em' }}></div></Item2>
      </Grid>
      
      <ErrorDialog 
        isDialogOpened={isErrorDialogOpened} 
        errorMessage={errorMessage} 
        warningMessage={''} 
        onClose={onCloseErrorDialog} 
      />
      
      <Grid xs={12}>
        <Header title="NMR to Structure by AI" helpAddress="help#ai-prediction" showLogin={true} />
      </Grid>

      {isLoading ? (
        <ProgressControl isLoading={isLoading} />
      ) : (
        <>
          {/* First row - Predicted Spectrum */}
          <Grid container xs={12} style={{ marginTop: '2em', display: 'flex', flexDirection: 'column' }}>
           
              <Typography sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                flexGrow: 1
              }} variant="h6" gutterBottom>Predicted Spectrum</Typography>
              <Box sx={{ 
                display: 'flex', 
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                flexGrow: 1
              }}>
                {spectrumPreview?.graph ? (
                  <img alt='NMR Spectrum'
                    className='graph-qc'
                    src={spectrumDataUri}
                    style={{ 
                      width: '100%', 
                      maxHeight: '250px', 
                      objectFit: 'contain' 
                    }} 
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '250px',
                    width: '100%'
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      Spectrum visualization not available
                    </Typography>
                  </Box>
                )}
              </Box>

          </Grid>

          {/* Second row - Molecule and Chemical Shifts */}
          <Grid container xs={12} spacing={2} justifyContent="center">
            {/* Molecule on the left */}
            <Grid xs={12} md={4}>
              <Paper elevation={3} sx={{ 
                p: 3, 
                height: '300px', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" gutterBottom align="center"></Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  flexGrow: 1,
                }}>
                  {moleculeSvgContent ? (
                    <div className="molecule-display" 
                         style={{ 
                           height: '300px', 
                           width: '100%', 
                           background: moleculeDataUri,
                           backgroundSize: 'contain',
                           backgroundPosition: 'center',
                           backgroundRepeat: 'no-repeat'
                         }}>
                    </div>
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      Structure will appear here
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Chemical shifts on the right */}
            <Grid xs={12} md={4}>
              <Paper elevation={3} sx={{ 
                p: 2, 
                height: '308px', 
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="h6" gutterBottom align="center"></Typography>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                  <DataGrid
                    autoHeight
                    getRowHeight={() => 'auto'}
                    rows={atomPeaks}
                    columns={columns}
                    onCellDoubleClick={(params, event) => {
                      if (!event.ctrlKey) {
                        event.defaultMuiPrevented = true;
                      }
                    }}
                    disableSelectionOnClick
                    experimentalFeatures={{ newEditingApi: true }}
                    sx={{ 
                      '& .MuiDataGrid-main': { height: '100%' }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};
export default PredictSpectrumResult;